import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import * as echarts from "echarts";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
// import { environment } from "src/environments/environment";
import type { SupportDashboardDownloadData } from "../support-dashboard/support-dashboard.component";

declare let flatpickr: any;

interface PercentageDisplay {
  value: number;
  display: string;
}

interface VolumeByPriorityRow {
  priority: string;
  count: number;
  percentage: PercentageDisplay;
}

interface SlaComplianceOverallRow {
  sla_type: string;
  met: number;
  breached: number;
  total: number;
  compliance_percentage: PercentageDisplay;
}

interface SlaComplianceByPriorityRow {
  priority: string;
  resolution_met: number;
  resolution_breached: number;
  resolution_percentage: PercentageDisplay;
  response_met: number;
  response_breached: number;
  response_percentage: PercentageDisplay;
}

interface SlaComplianceByPriorityTotal {
  resolution_met: number;
  resolution_breached: number;
  resolution_percentage: PercentageDisplay;
  response_met: number;
  response_breached: number;
  response_percentage: PercentageDisplay;
}

interface TicketsByCreationTimeRow {
  time_period: string;
  ticket_count: number;
  percentage: PercentageDisplay;
  raw_period?: "business" | "after" | "all";
}

interface AssigneeOption {
  id: string;
  name: string;
  ticketCount: number;
}

interface AccountOption {
  id: string;
  name: string;
  ticketCount: number;
}

interface DatePresetOption {
  label: string;
  value: string;
  startDate?: string;
  endDate?: string;
}

interface TableState<T> {
  loading: boolean;
  errorMessage: string | null;
  responseBody: T | null;
}

function createTableState<T>(): TableState<T> {
  return {
    loading: false,
    errorMessage: null,
    responseBody: null,
  };
}

const EMPTY_PERCENTAGE: PercentageDisplay = {
  value: 0,
  display: "0.00%",
};

const EMPTY_SLA_PRIORITY_TOTAL: SlaComplianceByPriorityTotal = {
  resolution_met: 0,
  resolution_breached: 0,
  resolution_percentage: EMPTY_PERCENTAGE,
  response_met: 0,
  response_breached: 0,
  response_percentage: EMPTY_PERCENTAGE,
};

export interface DrilldownConfig {
  drilldown:
    | "kpi"
    | "by_priority"
    | "sla"
    | "sla_by_priority"
    | "by_hour_band"
    | "by_assignee"
    | "by_account";
  kpi?: "total" | "resolution_pct" | "response_pct";
  priority?: string;
  sla?: "resolution" | "response";
  outcome?: "hit" | "miss" | "all";
  period?: "business" | "after" | "all";
  assignee?: string;
  account?: string;
  title: string;
  expectedTotal?: number;
}

@Component({
  selector: "app-swayam2-dashboard",
  templateUrl: "./swayam2-dashboard.component.html",
  styleUrls: ["./swayam2-dashboard.component.css"],
})
export class Swayam2DashboardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("volumeByPriorityChart")
  volumeByPriorityChart: ElementRef<HTMLDivElement>;
  @ViewChild("slaComplianceOverallChart")
  slaComplianceOverallChart: ElementRef<HTMLDivElement>;
  @ViewChild("slaComplianceByPriorityChart")
  slaComplianceByPriorityChart: ElementRef<HTMLDivElement>;
  @ViewChild("ticketsByCreationTimeChart")
  ticketsByCreationTimeChart: ElementRef<HTMLDivElement>;

  // View mode navigation: 'dashboard' | 'drilldown' | 'ticket-detail'
  viewMode: "dashboard" | "drilldown" | "ticket-detail" = "dashboard";
  dashboardContentView: "charts" | "tables" = "charts";

  accountId = localStorage.getItem("accountId") || "";
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  startDate = moment().startOf("month").format("YYYY-MM-DD");
  endDate = moment().format("YYYY-MM-DD");
  dateRange = "";
  datePresetOptions: DatePresetOption[] = [];
  selectedDatePreset = "";
  private flatpickrInstance: any = null;
  currentMessage: Subscription | null = null;

  accountOptions: AccountOption[] = [];
  selectedAccountDon = "ALL";

  assigneeOptions: AssigneeOption[] = [];
  selectedAssignees: string[] = [];
  loadingAssignees = false;

  totalTickets = 0;
  slaTrackedTickets: number | null = null;
  loadingTotalTickets = false;

  resolutionSLA = "0.00%";
  responseSLA = "0.00%";
  loadingSlaSummaryCards = true;

  volumeByPriorityTableState = createTableState<{
    summary: { total_tickets: number; rows: VolumeByPriorityRow[] };
  }>();

  slaComplianceOverallTableState = createTableState<{
    summary: { rows: SlaComplianceOverallRow[] };
  }>();

  slaComplianceByPriorityTableState = createTableState<{
    summary: {
      rows: SlaComplianceByPriorityRow[];
      total: SlaComplianceByPriorityTotal;
    };
  }>();

  ticketsByCreationTimeTableState = createTableState<{
    summary: { total_tickets: number; rows: TicketsByCreationTimeRow[] };
  }>();

  // ─── Drilldown State ────────────────────────────────────────────────────────
  currentDrilldownConfig: DrilldownConfig | null = null;
  drilldownTickets: any[] = [];
  loadingDrilldown = false;
  drilldownError: string | null = null;
  drilldownLimit = 50;
  drilldownOffset = 0;
  drilldownTotal = 0;

  // ─── Single Ticket Detail State ─────────────────────────────────────────────
  activeTicket: any = null;
  loadingTicketDetail = false;
  activeTicketMessages: any[] = [];
  loadingMessages = false;
  messagesNextCursor: string | null = null;

  private chartsReady = false;
  private chartRenderTimer: ReturnType<typeof setTimeout> | null = null;
  private charts: { [key: string]: echarts.ECharts } = {};

  private readonly priorityColors: { [key: string]: string } = {
    critical: "#dc2626",
    high: "#ea580c",
    medium: "#64748b",
    low: "#94a3b8",
  };

  private readonly defaultChartColors = [
    "#2563eb",
    "#64748b",
    "#0d9488",
    "#6366f1",
    "#d97706",
    "#475569",
  ];

  private readonly resolutionColor = "#2563eb";
  private readonly responseColor = "#64748b";
  private readonly metColor = "#2563eb";
  private readonly breachedColor = "#ea580c";

  constructor(
    private apiService: APIService,
    private notifier: NotifierService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.initDatePresets();
    const hasViewFromUrl = this.restoreStateFromUrl();
    this.subscribeToAccountChanges();
    this.initializeDateRangePicker();

    if (!hasViewFromUrl) {
      void this.loadDashboard();
      this.updateUrlParams();
    }
  }

  ngAfterViewInit(): void {
    this.chartsReady = true;
    this.scheduleChartRender();
    window.addEventListener("resize", this.resizeCharts);
  }

  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
    if (this.chartRenderTimer) {
      clearTimeout(this.chartRenderTimer);
    }
    window.removeEventListener("resize", this.resizeCharts);
    this.disposeCharts();
  }

  get volumeData(): VolumeByPriorityRow[] {
    return this.volumeByPriorityTableState.responseBody?.summary?.rows || [];
  }

  get slaOverallData(): SlaComplianceOverallRow[] {
    return this.slaComplianceOverallTableState.responseBody?.summary?.rows || [];
  }

  get slaPriorityData(): SlaComplianceByPriorityRow[] {
    return (
      this.slaComplianceByPriorityTableState.responseBody?.summary?.rows || []
    );
  }

  get slaPriorityTotal(): SlaComplianceByPriorityTotal {
    return (
      this.slaComplianceByPriorityTableState.responseBody?.summary?.total ||
      EMPTY_SLA_PRIORITY_TOTAL
    );
  }

  get creationTimeData(): TicketsByCreationTimeRow[] {
    return (
      this.ticketsByCreationTimeTableState.responseBody?.summary?.rows || []
    );
  }

  get dashboardSummaryTotalTickets(): number {
    return this.slaTrackedTickets ?? this.totalTickets;
  }

  get allAssigneesSelected(): boolean {
    return (
      this.assigneeOptions.length > 0 &&
      this.selectedAssignees.length === this.assigneeOptions.length
    );
  }

  get hasAssigneeFilter(): boolean {
    return (
      this.selectedAssignees.length > 0 &&
      this.selectedAssignees.length < this.assigneeOptions.length
    );
  }

  get selectedAssigneeSummary(): string {
    if (!this.selectedAssignees || !this.selectedAssignees.length) {
      return "All Assignees";
    }
    if (this.selectedAssignees.length === 1) {
      const match = this.assigneeOptions.find(
        (o) => o.id === this.selectedAssignees[0]
      );
      return match ? match.name : this.selectedAssignees[0];
    }
    return `${this.selectedAssignees.length} selected`;
  }

  get selectedAccountSummary(): string {
    const selectedAccount = this.accountOptions.find(
      (option) => option.id === this.selectedAccountDon
    );
    return selectedAccount?.name || this.selectedAccountDon || "All Accounts";
  }

  get isDownloadDisabled(): boolean {
    return (
      this.loadingTotalTickets ||
      this.loadingSlaSummaryCards ||
      this.volumeByPriorityTableState.loading ||
      this.slaComplianceOverallTableState.loading
    );
  }

  get downloadExportData(): SupportDashboardDownloadData {
    return {
      startDate: this.startDate,
      endDate: this.endDate,
      assigneeDisplay: this.getExportAssigneeDisplay(),
      totalTickets: this.totalTickets,
      resolutionSLA: this.resolutionSLA,
      responseSLA: this.responseSLA,
      avgMTTR: "0",
      volumeData: this.volumeData,
      slaOverallData: this.slaOverallData,
      slaPriorityData: this.slaPriorityData,
      slaPriorityTotal: this.slaPriorityTotal,
      mttrData: [],
      mttrOverall: {
        avg_resolution_time_hours: 0,
        ticket_count: 0,
      },
      creationTimeData: this.creationTimeData,
    };
  }

  private getExportAssigneeDisplay(): string {
    if (!this.selectedAssignees || !this.selectedAssignees.length) {
      return "All Assignees";
    }
    return this.selectedAssignees
      .map((id) => {
        const match = this.assigneeOptions.find((o) => o.id === id);
        return match ? match.name : id;
      })
      .join(", ");
  }

  selectAllAssignees(): void {
    if (this.allAssigneesSelected) {
      this.selectedAssignees = [];
    } else {
      this.selectedAssignees = this.assigneeOptions.map((o) => o.id);
    }
    this.updateUrlParams();
    void this.loadDashboard();
  }

  async onAssigneeSelectionChange(selection: any): Promise<void> {
    if (Array.isArray(selection)) {
      this.selectedAssignees = selection.map((item) =>
        typeof item === "object" ? item.id : item
      );
    } else {
      this.selectedAssignees = [];
    }
    this.updateUrlParams();
    await this.loadDashboard();
  }

  async onAccountSelectionChange(selection: any): Promise<void> {
    if (typeof selection === "string") {
      this.selectedAccountDon = selection;
    } else if (selection && typeof selection === "object") {
      this.selectedAccountDon = selection.id || "ALL";
    } else {
      this.selectedAccountDon = "ALL";
    }
    this.updateUrlParams();
    await this.loadDashboard();
  }

  private initDatePresets(): void {
    const options: DatePresetOption[] = [];
    const now = moment();

    // 1. Current month (default)
    const curStart = now.clone().startOf("month").format("YYYY-MM-DD");
    const curEnd = now.clone().format("YYYY-MM-DD");
    options.push({
      label: now.format("MMM YYYY"),
      value: now.format("YYYY-MM"),
      startDate: curStart,
      endDate: curEnd,
    });

    // 2. Last 5 months
    for (let i = 1; i <= 5; i++) {
      const prev = now.clone().subtract(i, "months");
      options.push({
        label: prev.format("MMM YYYY"),
        value: prev.format("YYYY-MM"),
        startDate: prev.clone().startOf("month").format("YYYY-MM-DD"),
        endDate: prev.clone().endOf("month").format("YYYY-MM-DD"),
      });
    }

    // 3. Custom Range
    options.push({
      label: "Custom Range",
      value: "custom",
    });

    this.datePresetOptions = options;
    this.selectedDatePreset = options[0].value;
    this.startDate = options[0].startDate!;
    this.endDate = options[0].endDate!;
  }

  async onDatePresetChange(presetValue: string): Promise<void> {
    this.selectedDatePreset = presetValue;

    if (presetValue === "custom") {
      this.updateUrlParams();
      if (this.flatpickrInstance) {
        setTimeout(() => this.flatpickrInstance.open(), 50);
      }
      return;
    }

    const preset = this.datePresetOptions.find((o) => o.value === presetValue);
    if (preset?.startDate && preset?.endDate) {
      this.startDate = preset.startDate;
      this.endDate = preset.endDate;
      this.syncDateRangeDisplay();
      if (this.flatpickrInstance) {
        this.flatpickrInstance.setDate(
          [new Date(this.startDate), new Date(this.endDate)],
          false
        );
      }
      this.updateUrlParams();
      await this.loadDashboard();
    }
  }

  private initializeDateRangePicker(): void {
    this.syncDateRangeDisplay();

    setTimeout(() => {
      const fp = flatpickr(".datetimepicker", {
        dateFormat: "d-M-Y",
        mode: "range",
        maxDate: new Date(),
        showMonths: 2,
        defaultDate: [new Date(this.startDate), new Date(this.endDate)],
        onChange: async (
          selectedDates: Date[],
          dateStr: string,
          instance: any
        ) => {
          if (!dateStr.includes("to")) {
            return;
          }

          this.selectedDatePreset = "custom";
          this.dateRange = dateStr;
          this.updateDateRangeValues(dateStr);
          this.updateUrlParams();
          await this.loadDashboard();
        },
      });
      this.flatpickrInstance = Array.isArray(fp) ? fp[0] : fp;
    }, 100);
  }

  private syncDateRangeDisplay(): void {
    this.dateRange =
      moment(this.startDate, "YYYY-MM-DD").format("DD-MMM-YYYY") +
      " to " +
      moment(this.endDate, "YYYY-MM-DD").format("DD-MMM-YYYY");
  }

  private updateDateRangeValues(dateRange: string): void {
    if (!(dateRange && dateRange.includes("to"))) {
      return;
    }

    const selectedDateRange = dateRange.toString().split(" to ");
    this.startDate = moment(selectedDateRange[0], "DD-MMM-YYYY").format(
      "YYYY-MM-DD"
    );
    this.endDate = moment(selectedDateRange[1], "DD-MMM-YYYY").format(
      "YYYY-MM-DD"
    );
  }

  buildCurrentUrlParams(): any {
    const params: any = {
      view: this.viewMode,
      startDate: this.startDate,
      endDate: this.endDate,
      preset: this.selectedDatePreset,
    };

    if (this.selectedAccountDon && this.selectedAccountDon !== "ALL") {
      params.account = this.selectedAccountDon;
    }

    if (this.selectedAssignees && this.selectedAssignees.length > 0) {
      params.assignees = this.selectedAssignees.join(",");
    }

    if (this.viewMode === "drilldown" || this.viewMode === "ticket-detail") {
      if (this.currentDrilldownConfig) {
        params.drilldown = this.currentDrilldownConfig.drilldown;
        if (this.currentDrilldownConfig.kpi) params.kpi = this.currentDrilldownConfig.kpi;
        if (this.currentDrilldownConfig.priority) params.priority = this.currentDrilldownConfig.priority;
        if (this.currentDrilldownConfig.sla) params.sla = this.currentDrilldownConfig.sla;
        if (this.currentDrilldownConfig.outcome) params.outcome = this.currentDrilldownConfig.outcome;
        if (this.currentDrilldownConfig.period) params.period = this.currentDrilldownConfig.period;
        if (this.currentDrilldownConfig.assignee) params.drilldownAssignee = this.currentDrilldownConfig.assignee;
        if (this.currentDrilldownConfig.account) params.drilldownAccount = this.currentDrilldownConfig.account;
        if (this.currentDrilldownConfig.title) params.drilldownTitle = this.currentDrilldownConfig.title;
        if (this.currentDrilldownConfig.expectedTotal != null) {
          params.expectedTotal = String(this.currentDrilldownConfig.expectedTotal);
        }
        params.limit = String(this.drilldownLimit);
        params.offset = String(this.drilldownOffset);
      }
    }

    if (this.viewMode === "ticket-detail" && this.activeTicket) {
      const ticketDon = this.getTicketDon(this.activeTicket);
      if (ticketDon) {
        params.ticket = ticketDon;
      }
    }

    return params;
  }

  updateUrlParams(): void {
    const queryParams = this.buildCurrentUrlParams();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private restoreStateFromUrl(): boolean {
    const params = this.route.snapshot.queryParamMap;
    const view = params.get("view");
    const startDate = params.get("startDate");
    const endDate = params.get("endDate");
    const preset = params.get("preset");
    const account = params.get("account");
    const assignees = params.get("assignees");

    // 1. Restore Date Range & Preset
    if (preset && this.datePresetOptions.some((o) => o.value === preset)) {
      this.selectedDatePreset = preset;
      if (preset !== "custom") {
        const match = this.datePresetOptions.find((o) => o.value === preset);
        if (match?.startDate && match?.endDate) {
          this.startDate = match.startDate;
          this.endDate = match.endDate;
        }
      } else if (this.isValidQueryDate(startDate) && this.isValidQueryDate(endDate)) {
        this.startDate = startDate;
        this.endDate = endDate;
      }
    } else if (this.isValidQueryDate(startDate) && this.isValidQueryDate(endDate)) {
      this.startDate = startDate;
      this.endDate = endDate;
      const match = this.datePresetOptions.find(
        (o) => o.startDate === startDate && o.endDate === endDate
      );
      this.selectedDatePreset = match ? match.value : "custom";
    }

    this.syncDateRangeDisplay();

    // 2. Restore Account
    if (account) {
      this.selectedAccountDon = account;
    }

    // 3. Restore Assignees
    if (assignees) {
      this.selectedAssignees = assignees.split(",").filter(Boolean);
    }

    // 4. Restore Drilldown / Ticket view
    const drilldownType = params.get("drilldown") as
      | DrilldownConfig["drilldown"]
      | null;
    if (drilldownType) {
      this.currentDrilldownConfig = {
        drilldown: drilldownType,
        kpi: (params.get("kpi") as any) || undefined,
        priority: params.get("priority") || undefined,
        sla: (params.get("sla") as any) || undefined,
        outcome: (params.get("outcome") as any) || undefined,
        period: (params.get("period") as any) || undefined,
        assignee: params.get("drilldownAssignee") || undefined,
        account: params.get("drilldownAccount") || undefined,
        title: params.get("drilldownTitle") || "Tickets",
        expectedTotal: Number(params.get("expectedTotal")) || undefined,
      };
      const limit = Number(params.get("limit"));
      if (limit > 0) this.drilldownLimit = limit;
      const offset = Number(params.get("offset"));
      if (offset >= 0) this.drilldownOffset = offset;
    }

    const ticketDon = params.get("ticket");

    if (view === "ticket-detail" && ticketDon) {
      this.viewMode = "ticket-detail";
      void this.openTicketDetail({ "Ticket DON": ticketDon, id: ticketDon });
      void this.loadDashboard();
      return true;
    } else if (view === "drilldown" && this.currentDrilldownConfig) {
      this.viewMode = "drilldown";
      void this.fetchDrilldownTickets();
      void this.loadDashboard();
      return true;
    }

    this.viewMode = "dashboard";
    return false;
  }

  private isValidQueryDate(date: string | null): date is string {
    return Boolean(date) && moment(date, "YYYY-MM-DD", true).isValid();
  }

  private subscribeToAccountChanges(): void {
    this.currentMessage = this.notifier.currentMessage.subscribe((msg: any) => {
      if (!msg) {
        return;
      }

      let notificationData: any;
      try {
        notificationData = typeof msg === "string" ? JSON.parse(msg) : msg;
      } catch {
        return;
      }

      if (
        notificationData?.value == null ||
        notificationData?.source !== "notifier"
      ) {
        return;
      }

      if (notificationData.key === "accountId") {
        const newAccountId = String(notificationData.value).trim();
        const currentAccountId = String(this.accountId || "").trim();

        // Only switch to dashboard if accountId actually changed from last time!
        if (currentAccountId && newAccountId && newAccountId !== currentAccountId) {
          this.accountId = newAccountId;
          this.selectedAccountDon = "ALL";
          this.accountOptions = [];
          this.selectedAssignees = [];
          this.assigneeOptions = [];
          this.backToDashboard();
          void this.loadDashboard();
        } else if (!currentAccountId && newAccountId) {
          this.accountId = newAccountId;
        }
      }
    });
  }

  private isDateRangeValid(): boolean {
    if (!this.startDate || !this.endDate) {
      this.notifier.alert(
        "Info",
        "",
        "Please select both start date and end date.",
        "info",
        5000
      );
      return false;
    }

    if (this.startDate > this.endDate) {
      this.notifier.alert(
        "Info",
        "",
        "Start date should be less than or equal to end date.",
        "info",
        5000
      );
      return false;
    }

    return true;
  }

  // ─── Account & DevRev Endpoint ──────────────────────────────────────────────

  private fetchSelectedAccountDetails(): any | null {
    const selectedAccounts = localStorage.getItem("accountIdData");
    if (!selectedAccounts || !this.accountId) {
      return null;
    }

    try {
      const parsedAccounts = JSON.parse(selectedAccounts);
      return (
        parsedAccounts?.list?.find((account: any) => {
          return account.accountId == this.accountId;
        }) || null
      );
    } catch {
      return null;
    }
  }

  private getDevRevAccount(): string {
    const selectedAccount = this.fetchSelectedAccountDetails();
    const devrevAccount =
      selectedAccount?.devrev_account ||
      selectedAccount?.devrev_account_id ||
      selectedAccount?.devrev_account_don ||
      selectedAccount?.devrev_don ||
      selectedAccount?.devrev_id ||
      selectedAccount?.devrev_org_id ||
      localStorage.getItem("swayam_devrev_account") ||
      localStorage.getItem("devrev_account_don");

    if (devrevAccount) {
      return devrevAccount.trim();
    }

    // Default CDOT DevRev Account DON
    return "don:identity:dvrv-us-1:devo/14Ppyd4mrr:account/fNvESR9o";
  }

  private getDevRevAccountScope(devrevAccount: string): string {
    return this.selectedAccountDon && this.selectedAccountDon !== "ALL"
      ? this.selectedAccountDon.trim()
      : devrevAccount;
  }

  private getDevRevApiUrl(): string {
    const baseUrl = APIService.API_ENDPOINTV3;
    return `${baseUrl}/admin/users/devrev_redirection`;
  }

  private buildDevRevPayload(widgetName: string, devrevAccount: string): any {
    const from = `${this.startDate}T00:00:00+05:30`;
    const to = `${this.endDate}T00:00:00+05:30`;

    const payload: any = {
      path: "/dashboard/widgets",
      accounts: this.getDevRevAccountScope(devrevAccount),
      from,
      to,
      widgets: widgetName,
    };

    // Assignees must be passed as comma-separated string of DONs
    if (this.selectedAssignees && this.selectedAssignees.length > 0) {
      payload.assignees = this.selectedAssignees.join(",");
    }

    return payload;
  }

  private async callDevRevApi(payload: any): Promise<any> {
    const headers = { Authorization: localStorage.getItem("t") };

    try {
      const rawResult = await this.apiService.postDataPromis(
        this.getDevRevApiUrl(),
        payload,
        headers
      );

      let result = rawResult;
      if (typeof result === "string") {
        try {
          result = JSON.parse(result);
        } catch {
          return null;
        }
      }
      if (result?.body) {
        result =
          typeof result.body === "string" ? JSON.parse(result.body) : result.body;
      }
      return result;
    } catch (e) {
      console.error(`DevRev API error for path ${payload?.path}:`, e);
      return null;
    }
  }

  private extractNumericValue(val: any, fallback = 0): number {
    if (val == null) return fallback;
    if (typeof val === "number") return isNaN(val) ? fallback : val;
    if (typeof val === "object") {
      if (val.parsedValue != null && !isNaN(Number(val.parsedValue))) {
        return Number(val.parsedValue);
      }
      if (val.source != null && !isNaN(Number(val.source))) {
        return Number(val.source);
      }
      if (val.value != null && !isNaN(Number(val.value))) {
        return Number(val.value);
      }
    }
    const parsed = parseFloat(String(val).replace(/%/g, "").trim());
    return isNaN(parsed) ? fallback : parsed;
  }

  private extractPercentageDisplay(
    val: any,
    fallbackRatio = 0
  ): PercentageDisplay {
    if (val != null) {
      if (typeof val === "object") {
        if (typeof val.display === "string" && typeof val.value === "number") {
          return val;
        }
        const num =
          val.parsedValue != null && !isNaN(Number(val.parsedValue))
            ? Number(val.parsedValue)
            : val.source != null && !isNaN(Number(val.source))
            ? Number(val.source)
            : val.value != null && !isNaN(Number(val.value))
            ? Number(val.value)
            : NaN;
        if (!isNaN(num)) {
          const ratio = num > 1 ? num / 100 : num;
          const displayStr =
            val.source != null && !isNaN(Number(val.source))
              ? `${Number(val.source).toFixed(2)}%`
              : `${(ratio * 100).toFixed(2)}%`;
          return {
            value: ratio,
            display: displayStr,
          };
        }
      } else {
        const num =
          typeof val === "number"
            ? val
            : parseFloat(String(val).replace(/%/g, "").trim());
        if (!isNaN(num)) {
          const ratio = num > 1 ? num / 100 : num;
          return {
            value: ratio,
            display: `${(ratio * 100).toFixed(2)}%`,
          };
        }
      }
    }

    return {
      value: fallbackRatio,
      display: `${(fallbackRatio * 100).toFixed(2)}%`,
    };
  }

  // ─── Data Loaders ───────────────────────────────────────────────────────────

  async loadDashboard(): Promise<void> {
    if (!this.isDateRangeValid()) {
      this.loadingSlaSummaryCards = false;
      this.loadingTotalTickets = false;
      return;
    }

    const devrevAccount = this.getDevRevAccount();

    await Promise.all([
      this.loadTotalTickets(devrevAccount),
      this.loadAssigneeOptions(devrevAccount),
      this.loadSlaSummary(devrevAccount),
      this.loadVolumeByPriority(devrevAccount),
      this.loadSlaByType(devrevAccount),
      this.loadSlaComplianceByPriority(devrevAccount),
      this.loadBusinessHoursSplit(devrevAccount),
    ]);

    this.loadAccountOptions(devrevAccount);
    this.scheduleChartRender();
  }

  private async loadTotalTickets(devrevAccount: string): Promise<void> {
    this.loadingTotalTickets = true;
    try {
      const payload = this.buildDevRevPayload("total_tickets", devrevAccount);
      const res = await this.callDevRevApi(payload);
      const widgetData = res?.widgets?.total_tickets;
      const rows = widgetData?.rows || [];
      if (rows.length > 0) {
        this.totalTickets = Number(
          rows[0]["Ticket Count"] ?? rows[0]["ticket_count"] ?? 0
        );
      } else {
        this.totalTickets = 0;
      }
    } catch (e) {
      console.error("Error loading DevRev total tickets", e);
      this.totalTickets = 0;
    } finally {
      this.loadingTotalTickets = false;
    }
  }

  private async loadAssigneeOptions(devrevAccount: string): Promise<void> {
    this.loadingAssignees = true;
    try {
      const payload = this.buildDevRevPayload("by_assignee", devrevAccount);
      const res = await this.callDevRevApi(payload);
      const widgetData = res?.widgets?.by_assignee;
      const rows = widgetData?.rows || [];

      this.assigneeOptions = rows
        .map((row: any): AssigneeOption | null => {
          const name = String(
            row["Assignee Name"] ?? row["assignee_name"] ?? row["name"] ?? ""
          ).trim();
          const id = String(
            row["Assignee ID"] ??
              row["assignee_id"] ??
              row["Assignee DON"] ??
              row["assignee_don"] ??
              ""
          ).trim();
          const count = Number(
            row["Ticket Count"] ??
              row["ticket_count"] ??
              row["Count"] ??
              row["count"] ??
              0
          );
          if (!name || !id.startsWith("don:identity:")) return null;
          return { id, name, ticketCount: count };
        })
        .filter(
          (opt: AssigneeOption | null): opt is AssigneeOption => opt !== null
        );
    } catch (e) {
      console.error("Error loading DevRev assignees", e);
      this.assigneeOptions = [];
    } finally {
      this.loadingAssignees = false;
    }
  }

  private async loadSlaSummary(devrevAccount: string): Promise<void> {
    this.loadingSlaSummaryCards = true;
    this.slaTrackedTickets = null;
    try {
      const payload = this.buildDevRevPayload("sla_summary", devrevAccount);
      const res = await this.callDevRevApi(payload);
      const widgetData = res?.widgets?.sla_summary;
      const rows = widgetData?.rows || [];

      if (rows.length > 0) {
        const row = rows[0];
        const trackedValue =
          row["sla_tracked_tickets"] ??
          row["sla_tracked_ticket_count"] ??
          row["Tracked Count"] ??
          row.tracked_count ??
          row["Tracked Ticket Count"] ??
          row.tracked_ticket_count ??
          row["Tracked Tickets"] ??
          row.tracked_tickets;
        const trackedCount = this.extractNumericValue(trackedValue, NaN);
        this.slaTrackedTickets =
          trackedValue != null && Number.isFinite(trackedCount)
            ? trackedCount
            : null;

        const resPctObj = this.extractPercentageDisplay(
          row.resolution_sla_pct ?? row["Resolution SLA"] ?? row.resolution_sla
        );
        const respPctObj = this.extractPercentageDisplay(
          row.response_sla_pct ?? row["Response SLA"] ?? row.response_sla
        );

        this.resolutionSLA = resPctObj.display;
        this.responseSLA = respPctObj.display;
      } else {
        this.resolutionSLA = "0.00%";
        this.responseSLA = "0.00%";
      }
    } catch (e) {
      console.error("Error loading DevRev SLA summary", e);
      this.slaTrackedTickets = null;
      this.resolutionSLA = "0.00%";
      this.responseSLA = "0.00%";
    } finally {
      this.loadingSlaSummaryCards = false;
    }
  }

  private async loadVolumeByPriority(devrevAccount: string): Promise<void> {
    this.volumeByPriorityTableState.loading = true;
    try {
      const payload = this.buildDevRevPayload("by_priority", devrevAccount);
      const res = await this.callDevRevApi(payload);
      const widgetData =
        res?.widgets?.by_priority || res?.widgets?.volume_by_priority;
      const rows = widgetData?.rows || [];

      if (rows.length > 0) {
        const nonTotalRows = rows.filter(
          (r: any) =>
            (r["Priority"] || r["priority"] || "").toLowerCase() !== "total"
        );
        const totalRow = rows.find(
          (r: any) =>
            (r["Priority"] || r["priority"] || "").toLowerCase() === "total"
        );

        const total =
          (totalRow
            ? Number(
                totalRow["Ticket Count"] ||
                  totalRow["Count"] ||
                  totalRow["count"] ||
                  0
              )
            : null) ||
          nonTotalRows.reduce(
            (sum: number, r: any) =>
              sum +
              Number(r["Ticket Count"] || r["Count"] || r["count"] || 0),
            0
          ) ||
          this.totalTickets;

        const mappedRows: VolumeByPriorityRow[] = nonTotalRows.map((r: any) => {
          const priority = r["Priority"] || r["priority"] || "Unknown";
          const count = Number(
            r["Ticket Count"] || r["Count"] || r["count"] || 0
          );
          const fallbackRatio = total > 0 ? count / total : 0;
          const pct = this.extractPercentageDisplay(
            r["Percentage"] ?? r["percentage"],
            fallbackRatio
          );
          return {
            priority,
            count,
            percentage: pct,
          };
        });

        this.volumeByPriorityTableState.responseBody = {
          summary: { total_tickets: total, rows: mappedRows },
        };
      } else {
        this.volumeByPriorityTableState.responseBody = null;
      }
    } catch {
      this.volumeByPriorityTableState.responseBody = null;
    } finally {
      this.volumeByPriorityTableState.loading = false;
      this.scheduleChartRender();
    }
  }

  // 5. SLA by type (Resolution and Response, met vs breached)
  private async loadSlaByType(devrevAccount: string): Promise<void> {
    this.slaComplianceOverallTableState.loading = true;
    try {
      const payload = this.buildDevRevPayload("sla_by_type", devrevAccount);
      const res = await this.callDevRevApi(payload);
      const widgetData = res?.widgets?.sla_by_type;
      const rows = widgetData?.rows || [];

      if (rows.length > 0) {
        const mappedRows: SlaComplianceOverallRow[] = rows.map((r: any) => {
          const slaTypeRaw = r["SLA Type"] || r["sla_type"] || r["sla"] || "";
          const slaType =
            slaTypeRaw.toLowerCase().includes("resolution")
              ? "Resolution SLA"
              : slaTypeRaw.toLowerCase().includes("response")
              ? "Response SLA"
              : slaTypeRaw || "SLA";

          const met = Number(r["Met"] ?? r["met"] ?? r["hit"] ?? 0);
          const breached = Number(
            r["Breached"] ?? r["breached"] ?? r["miss"] ?? 0
          );
          const total = Number(
            r["Total"] ?? r["total"] ?? r["all"] ?? met + breached
          );
          const fallbackRatio = total > 0 ? met / total : 0;
          const pct = this.extractPercentageDisplay(
            r["Compliance %"] ?? r["compliance_pct"],
            fallbackRatio
          );

          return {
            sla_type: slaType,
            met,
            breached,
            total,
            compliance_percentage: pct,
          };
        });

        this.slaComplianceOverallTableState.responseBody = {
          summary: { rows: mappedRows },
        };
      } else {
        this.slaComplianceOverallTableState.responseBody = null;
      }
    } catch {
      this.slaComplianceOverallTableState.responseBody = null;
    } finally {
      this.slaComplianceOverallTableState.loading = false;
      this.scheduleChartRender();
    }
  }

  // 6. SLA by priority
  private async loadSlaComplianceByPriority(
    devrevAccount: string
  ): Promise<void> {
    this.slaComplianceByPriorityTableState.loading = true;
    try {
      const payload = this.buildDevRevPayload(
        "sla_by_priority",
        devrevAccount
      );
      const res = await this.callDevRevApi(payload);
      const widgetData =
        res?.widgets?.sla_by_priority ||
        res?.widgets?.sla_compliance_by_priority;
      const rows = widgetData?.rows || [];

      if (rows.length > 0) {
        const nonTotalRows = rows.filter(
          (r: any) =>
            (r["Priority"] || r["priority"] || "").toLowerCase() !== "total"
        );
        const totalRow = rows.find(
          (r: any) =>
            (r["Priority"] || r["priority"] || "").toLowerCase() === "total"
        );

        const mappedRows: SlaComplianceByPriorityRow[] = nonTotalRows.map(
          (r: any) => {
            const resMet = Number(
              r["Resolution Met"] || r["resolution_met"] || r["resolution_hit"] || 0
            );
            const resBreached = Number(
              r["Resolution Breached"] ||
                r["resolution_breached"] ||
                r["resolution_miss"] ||
                0
            );
            const resTotal = resMet + resBreached;
            const resFallbackRatio = resTotal > 0 ? resMet / resTotal : 0;
            const resPct = this.extractPercentageDisplay(
              r["Resolution %"] ?? r["resolution_pct"],
              resFallbackRatio
            );

            const respMet = Number(
              r["Response Met"] || r["response_met"] || r["response_hit"] || 0
            );
            const respBreached = Number(
              r["Response Breached"] ||
                r["response_breached"] ||
                r["response_miss"] ||
                0
            );
            const respTotal = respMet + respBreached;
            const respFallbackRatio = respTotal > 0 ? respMet / respTotal : 0;
            const respPct = this.extractPercentageDisplay(
              r["Response %"] ?? r["response_pct"],
              respFallbackRatio
            );

            return {
              priority: r["Priority"] || r["priority"] || "Unknown",
              resolution_met: resMet,
              resolution_breached: resBreached,
              resolution_percentage: resPct,
              response_met: respMet,
              response_breached: respBreached,
              response_percentage: respPct,
            };
          }
        );

        const totResMet = totalRow
          ? Number(totalRow["Resolution Met"] ?? totalRow["resolution_met"] ?? 0)
          : mappedRows.reduce((acc, r) => acc + r.resolution_met, 0);
        const totResBreached = totalRow
          ? Number(totalRow["Resolution Breached"] ?? totalRow["resolution_breached"] ?? 0)
          : mappedRows.reduce((acc, r) => acc + r.resolution_breached, 0);
        const totRes = totResMet + totResBreached;
        const totResFallbackRatio = totRes > 0 ? totResMet / totRes : 0;
        const totResPct = this.extractPercentageDisplay(
          totalRow?.["Resolution %"] ?? totalRow?.["resolution_pct"],
          totResFallbackRatio
        );

        const totRespMet = totalRow
          ? Number(totalRow["Response Met"] ?? totalRow["response_met"] ?? 0)
          : mappedRows.reduce((acc, r) => acc + r.response_met, 0);
        const totRespBreached = totalRow
          ? Number(totalRow["Response Breached"] ?? totalRow["response_breached"] ?? 0)
          : mappedRows.reduce((acc, r) => acc + r.response_breached, 0);
        const totResp = totRespMet + totRespBreached;
        const totRespFallbackRatio = totResp > 0 ? totRespMet / totResp : 0;
        const totRespPct = this.extractPercentageDisplay(
          totalRow?.["Response %"] ?? totalRow?.["response_pct"],
          totRespFallbackRatio
        );

        this.slaComplianceByPriorityTableState.responseBody = {
          summary: {
            rows: mappedRows,
            total: {
              resolution_met: totResMet,
              resolution_breached: totResBreached,
              resolution_percentage: totResPct,
              response_met: totRespMet,
              response_breached: totRespBreached,
              response_percentage: totRespPct,
            },
          },
        };
      } else {
        this.slaComplianceByPriorityTableState.responseBody = null;
      }
    } catch {
      this.slaComplianceByPriorityTableState.responseBody = null;
    } finally {
      this.slaComplianceByPriorityTableState.loading = false;
      this.scheduleChartRender();
    }
  }

  // 7. Business hours split (Creation time api - widgets: "by_hour_band")
  private async loadBusinessHoursSplit(
    devrevAccount: string
  ): Promise<void> {
    this.ticketsByCreationTimeTableState.loading = true;
    try {
      const payload = this.buildDevRevPayload("by_hour_band", devrevAccount);
      const res = await this.callDevRevApi(payload);
      const widgetData =
        res?.widgets?.by_hour_band || res?.widgets?.by_creation_time;
      const rows = widgetData?.rows || [];

      if (rows.length > 0) {
        const getRawPeriod = (row: any): "business" | "after" | "all" => {
          const label = String(
            row["Time Period"] ??
              row["time_period"] ??
              row["Hour Band"] ??
              row["hour_band"] ??
              row["Period"] ??
              row["period"] ??
              ""
          ).trim().toLowerCase();
          if (label === "total" || label === "all" || label.includes("total")) return "all";
          if (label.includes("after") || label.includes("non") || label.includes("before")) return "after";
          return "business";
        };

        const getCount = (row: any): number =>
          Number(
            row["Ticket Count"] ??
              row["ticket_count"] ??
              row["Count"] ??
              row["count"] ??
              0
          );

        const nonTotalRows = rows.filter((row: any) => getRawPeriod(row) !== "all");
        const totalRow = rows.find((row: any) => getRawPeriod(row) === "all");
        const total = totalRow
          ? getCount(totalRow)
          : nonTotalRows.reduce(
              (sum: number, row: any) => sum + getCount(row),
              0
            );

        const mappedRows: TicketsByCreationTimeRow[] = nonTotalRows.map(
          (r: any) => {
            const rawPeriod = getRawPeriod(r);
            const apiPeriodName = String(
              r["Time Period"] ??
                r["time_period"] ??
                r["Hour Band"] ??
                r["hour_band"] ??
                r["Period"] ??
                r["period"] ??
                ""
            ).trim();
            const displayPeriod =
              apiPeriodName ||
              (rawPeriod === "after" ? "After Hours" : "Business Hours");
            const count = getCount(r);
            const fallbackRatio = total > 0 ? count / total : 0;
            const pct = this.extractPercentageDisplay(
              r["Percentage"] ?? r["percentage"],
              fallbackRatio
            );

            return {
              time_period: displayPeriod,
              raw_period: rawPeriod,
              ticket_count: count,
              percentage: pct,
            };
          }
        );

        const totalPct = this.extractPercentageDisplay(
          totalRow?.["Percentage"] ?? totalRow?.["percentage"],
          total > 0 ? 1 : 0
        );

        mappedRows.push({
          time_period: "Total",
          raw_period: "all",
          ticket_count: total,
          percentage: totalPct,
        });

        this.ticketsByCreationTimeTableState.responseBody = {
          summary: { total_tickets: total, rows: mappedRows },
        };
      } else {
        this.ticketsByCreationTimeTableState.responseBody = null;
      }
    } catch {
      this.ticketsByCreationTimeTableState.responseBody = null;
    } finally {
      this.ticketsByCreationTimeTableState.loading = false;
      this.scheduleChartRender();
    }
  }

  private loadAccountOptions(devrevAccount: string): void {
    const selectedAccount = this.fetchSelectedAccountDetails();
    const accountName = String(
      selectedAccount?.accountName ||
        selectedAccount?.account_name ||
        selectedAccount?.name ||
        "Current Account"
    ).trim();

    this.accountOptions = [
      { id: "ALL", name: "All Accounts", ticketCount: this.totalTickets },
      {
        id: devrevAccount,
        name: accountName || devrevAccount,
        ticketCount: this.totalTickets,
      },
    ];
  }

  // ─── Drilldowns & Navigation ────────────────────────────────────────────────

  async openDrilldown(config: DrilldownConfig): Promise<void> {
    this.currentDrilldownConfig = config;
    this.drilldownOffset = 0;
    this.drilldownLimit = 50;
    this.viewMode = "drilldown";
    this.updateUrlParams();
    await this.fetchDrilldownTickets();
  }

  async fetchDrilldownTickets(): Promise<void> {
    if (!this.currentDrilldownConfig) return;

    this.loadingDrilldown = true;
    this.drilldownError = null;
    const devrevAccount = this.getDevRevAccount();

    const from = `${this.startDate}T00:00:00+05:30`;
    const to = `${this.endDate}T00:00:00+05:30`;

    const payload: any = {
      path: "/dashboard/drilldown",
      accounts: this.getDevRevAccountScope(devrevAccount),
      from,
      to,
      drilldown: this.currentDrilldownConfig.drilldown,
      limit: this.drilldownLimit,
      offset: this.drilldownOffset,
    };

    if (this.currentDrilldownConfig.kpi) {
      payload.kpi = this.currentDrilldownConfig.kpi;
    }
    if (this.currentDrilldownConfig.priority) {
      payload.priority = this.currentDrilldownConfig.priority;
    }
    if (this.currentDrilldownConfig.sla) {
      payload.sla = this.currentDrilldownConfig.sla;
    }
    if (this.currentDrilldownConfig.outcome) {
      payload.outcome = this.currentDrilldownConfig.outcome;
    }
    if (this.currentDrilldownConfig.period) {
      payload.period = this.currentDrilldownConfig.period;
    }
    if (this.currentDrilldownConfig.assignee) {
      payload.assignee = this.currentDrilldownConfig.assignee;
    } else if (this.selectedAssignees && this.selectedAssignees.length > 0) {
      payload.assignees = this.selectedAssignees.join(",");
    }
    if (this.currentDrilldownConfig.account) {
      payload.account = this.currentDrilldownConfig.account;
    } else if (this.currentDrilldownConfig.drilldown === "by_account") {
      payload.account = this.selectedAccountDon || "ALL";
    }

    try {
      const res = await this.callDevRevApi(payload);
      const rows =
        res?.rows ||
        res?.drilldown?.rows ||
        res?.tickets ||
        res?.data ||
        (Array.isArray(res) ? res : []);

      const backendTotal = res?.total ?? res?.num_rows ?? res?.count;
      const expected = this.currentDrilldownConfig?.expectedTotal;

      // Some backend responses may contain more rows than the requested page.
      if (rows.length > this.drilldownLimit) {
        this.drilldownTotal = Number(backendTotal ?? rows.length);
        this.drilldownTickets = rows.slice(
          this.drilldownOffset,
          this.drilldownOffset + this.drilldownLimit
        );
      } else {
        this.drilldownTickets = rows;
        if (expected != null && expected > 0) {
          this.drilldownTotal =
            backendTotal != null && Number(backendTotal) > rows.length
              ? Number(backendTotal)
              : Math.max(Number(backendTotal || 0), expected);
        } else {
          this.drilldownTotal = Number(backendTotal ?? rows.length);
        }
      }
    } catch (e) {
      console.error("Error fetching drilldown tickets:", e);
      this.drilldownError = "Failed to load drilldown tickets.";
      this.drilldownTickets = [];
      this.drilldownTotal = 0;
    } finally {
      this.loadingDrilldown = false;
    }
  }

  backToDashboard(): void {
    this.viewMode = "dashboard";
    this.activeTicket = null;
    this.activeTicketMessages = [];
    this.currentDrilldownConfig = null;
    this.updateUrlParams();
    setTimeout(() => {
      this.resizeCharts();
    }, 60);
  }

  drilldownAssignee(item: AssigneeOption, event: Event): void {
    event.stopPropagation();
    void this.openDrilldown({
      drilldown: "by_assignee",
      assignee: item.id,
      title: `Assignee: ${item.name}`,
      expectedTotal: item.ticketCount,
    });
  }

  drilldownAccount(item: AccountOption, event: Event): void {
    event.stopPropagation();
    void this.openDrilldown({
      drilldown: "by_account",
      account: item.id,
      title: `Account: ${item.name}`,
      expectedTotal: item.ticketCount,
    });
  }

  async backToDrilldown(): Promise<void> {
    if (!this.currentDrilldownConfig) {
      this.backToDashboard();
      return;
    }
    this.viewMode = "drilldown";
    this.activeTicket = null;
    this.activeTicketMessages = [];
    this.updateUrlParams();

    if (!this.drilldownTickets || this.drilldownTickets.length === 0) {
      await this.fetchDrilldownTickets();
    }
  }

  onLimitChange(limit: number): void {
    this.drilldownLimit = Math.min(200, Math.max(1, Number(limit) || 50));
    this.drilldownOffset = 0;
    this.updateUrlParams();
    void this.fetchDrilldownTickets();
  }

  prevPage(): void {
    if (this.drilldownOffset >= this.drilldownLimit) {
      this.drilldownOffset -= this.drilldownLimit;
      this.updateUrlParams();
      void this.fetchDrilldownTickets();
    }
  }

  nextPage(): void {
    if (this.drilldownOffset + this.drilldownLimit < this.drilldownTotal) {
      this.drilldownOffset += this.drilldownLimit;
      this.updateUrlParams();
      void this.fetchDrilldownTickets();
    }
  }

  get currentPage(): number {
    return Math.floor(this.drilldownOffset / this.drilldownLimit) + 1;
  }

  get totalPages(): number {
    if (!this.drilldownTotal) return 1;
    return Math.ceil(this.drilldownTotal / this.drilldownLimit) || 1;
  }

  // ─── Single Ticket & Messages ───────────────────────────────────────────────

  async openTicketDetail(ticket: any): Promise<void> {
    const ticketDon =
      typeof ticket === "string" ? ticket : this.getTicketDon(ticket);
    if (!ticketDon) {
      this.notifier.alert("Info", "", "Ticket DON not found.", "info", 5000);
      return;
    }

    this.activeTicket =
      typeof ticket === "object" && ticket !== null
        ? ticket
        : { "Ticket DON": ticketDon, id: ticketDon };
    this.activeTicketMessages = [];
    this.viewMode = "ticket-detail";
    this.loadingTicketDetail = true;
    this.loadingMessages = true; // Show correspondence loader immediately at start
    this.updateUrlParams();

    const devrevAccount = this.getDevRevAccount();
    const from = `${this.startDate}T00:00:00+05:30`;
    const to = `${this.endDate}T00:00:00+05:30`;

    const hasTicketAccess = await this.fetchTicketDetails(
      ticketDon,
      devrevAccount,
      from,
      to
    );
    if (hasTicketAccess) {
      await this.fetchTicketMessages(ticketDon);
    } else {
      this.loadingMessages = false;
      this.messagesNextCursor = null;
    }
  }

  private async fetchTicketDetails(
    ticketDon: string,
    devrevAccount: string,
    from: string,
    to: string
  ): Promise<boolean> {
    this.loadingTicketDetail = true;
    try {
      const ticketPayload = {
        path: "/dashboard/ticket",
        accounts: this.getDevRevAccountScope(devrevAccount),
        from,
        to,
        ticket: ticketDon,
      };
      const ticketRes = await this.callDevRevApi(ticketPayload);
      if (
        !ticketRes ||
        ticketRes.status === "0" ||
        ticketRes.s === "0" ||
        ticketRes.error
      ) {
        return false;
      }

      const ticketDetails =
        ticketRes.ticket && typeof ticketRes.ticket === "object"
          ? ticketRes.ticket
          : ticketRes;
      this.activeTicket = { ...this.activeTicket, ...ticketDetails };
      return true;
    } catch (e) {
      console.error("Error loading ticket details:", e);
      return false;
    } finally {
      this.loadingTicketDetail = false;
    }
  }

  async fetchTicketMessages(ticketDon: string, cursor?: string): Promise<void> {
    this.loadingMessages = true;
    const devrevAccount = this.getDevRevAccount();
    const from = `${this.startDate}T00:00:00+05:30`;
    const to = `${this.endDate}T00:00:00+05:30`;

    try {
      const messagesPayload: any = {
        path: "/dashboard/messages",
        accounts: this.getDevRevAccountScope(devrevAccount),
        from,
        to,
        ticket: ticketDon,
        limit: 50,
      };
      if (cursor) {
        messagesPayload.cursor = cursor;
      }

      const res = await this.callDevRevApi(messagesPayload);
      const responseMessages = res?.comments || res?.timeline_entries;
      const messages = Array.isArray(responseMessages)
        ? responseMessages.filter(
            (entry: any) =>
              entry?.type === "timeline_comment" &&
              entry?.visibility === "external"
          )
        : [];

      const getMsgTime = (m: any): number => {
        const raw = m?.created_date || m?.timestamp || m?.created_at;
        if (!raw) return 0;
        const time = new Date(raw).getTime();
        return isNaN(time) ? 0 : time;
      };

      let combined = cursor
        ? [...this.activeTicketMessages, ...messages]
        : messages;

      combined.sort(
        (a: any, b: any) => getMsgTime(a) - getMsgTime(b)
      );
      this.activeTicketMessages = combined;
      this.messagesNextCursor = res?.next_cursor || null;
    } catch (e) {
      console.error("Error loading ticket messages:", e);
    } finally {
      this.loadingMessages = false;
    }
  }

  loadMoreMessages(): void {
    if (this.messagesNextCursor && this.activeTicket) {
      const ticketDon = this.getTicketDon(this.activeTicket);
      void this.fetchTicketMessages(ticketDon, this.messagesNextCursor);
    }
  }

  // ─── Ticket Field Helpers ───────────────────────────────────────────────────

  getTicketDon(t: any): string {
    if (t?.["Ticket DON"]) return t["Ticket DON"];
    if (t?.["ticket_don"]) return t["ticket_don"];
    if (t?.["Ticket Don"]) return t["Ticket Don"];
    if (t?.["ticketDon"]) return t["ticketDon"];
    if (typeof t?.ticket === "string" && t.ticket.startsWith("don:")) return t.ticket;
    if (typeof t?.id === "string" && t.id.startsWith("don:")) return t.id;
    if (typeof t?.don === "string" && t.don.startsWith("don:")) return t.don;
    return "";
  }

  getTicketDisplayId(t: any): string {
    return (
      t?.["Ticket ID"] ||
      t?.display_id ||
      t?.issue_key ||
      t?.key ||
      t?.["Display ID"] ||
      t?.ticket ||
      "Ticket"
    );
  }

  getTicketTitle(t: any): string {
    return (
      t?.["Title"] ||
      t?.title ||
      t?.["Summary"] ||
      t?.summary ||
      "-"
    );
  }

  getTicketPriority(t: any): string {
    return (
      t?.["Priority"] ||
      t?.priority ||
      t?.["Severity"] ||
      t?.severity ||
      "-"
    );
  }

  getTicketStatus(t: any): string {
    return t?.["Status"] || t?.status || t?.["Stage"] || t?.stage || "-";
  }

  getTicketAssignee(t: any): string {
    const assignees =
      t?.["Assignees"] ||
      t?.["Assignee"] ||
      t?.assignee ||
      t?.["Owner"] ||
      t?.owner ||
      t?.["Assignee Name"] ||
      "";
    if (Array.isArray(assignees)) {
      return (
        assignees
          .map((assignee: any) =>
            typeof assignee === "string"
              ? assignee
              : assignee?.display_name || assignee?.name || assignee?.id || ""
          )
          .filter(Boolean)
          .join(", ") || "-"
      );
    }
    if (assignees && typeof assignees === "object") {
      return assignees.display_name || assignees.name || assignees.id || "-";
    }
    return assignees || "-";
  }

  getTicketSubtype(t: any): string {
    return t?.["Subtype"] || t?.subtype || t?.["Type"] || t?.type || "-";
  }

  getTicketAccount(t: any): string {
    return t?.["Account"] || t?.account || t?.account_name || "-";
  }

  getTicketReporter(t: any): string {
    const reporters =
      t?.["Reporters"] || t?.reporters || t?.["Reporter"] || t?.reporter;
    const formatReporter = (value: any): string => {
      const name = String(value ?? "").trim();
      const emailMatch = name.match(/^([^@\s]+)@([^@\s]+)$/);
      if (!emailMatch) return name;

      return emailMatch[1]
        .replace(/[._-]+/g, " ")
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
    };

    if (Array.isArray(reporters)) {
      return (
        reporters
          .map((reporter: any) => {
            const name =
              typeof reporter === "string"
                ? reporter
                : reporter?.display_name ||
                  reporter?.name ||
                  reporter?.email ||
                  reporter?.id ||
                  "";
            return formatReporter(name);
          })
          .filter(Boolean)
          .join(", ") || "-"
      );
    }
    if (reporters && typeof reporters === "object") {
      return formatReporter(
        reporters.display_name ||
          reporters.name ||
          reporters.email ||
          reporters.id ||
          "-"
      );
    }
    return formatReporter(reporters || "-");
  }

  getTicketReporterEmail(t: any): string {
    const reporters =
      t?.["Reporters"] || t?.reporters || t?.["Reporter"] || t?.reporter;
    const values = Array.isArray(reporters) ? reporters : [reporters];
    return values
      .map((reporter: any) =>
        typeof reporter === "string" ? reporter : reporter?.email || ""
      )
      .map((email: string) => email.trim())
      .filter((email: string) => email.includes("@"))
      .join(", ");
  }

  getTicketCreated(t: any): string {
    const raw =
      t?.["Created Date"] ||
      t?.["Created On"] ||
      t?.created_date ||
      t?.created_on ||
      t?.["Created"] ||
      t?.created ||
      t?.created_at;
    if (!raw) return "-";
    return moment(raw).isValid()
      ? moment(raw).format("DD-MMM-YYYY HH:mm")
      : raw;
  }

  getTicketClosed(t: any): string {
    const raw =
      t?.["Closed On"] ||
      t?.closed_on ||
      t?.["Closed Date"] ||
      t?.closed_date;
    if (!raw) return "-";
    return moment(raw).isValid()
      ? moment(raw).format("DD-MMM-YYYY HH:mm")
      : raw;
  }

  getTicketDescription(t: any): string {
    return t?.body || t?.description || t?.["Body"] || "-";
  }

  getMessageAuthor(m: any): string {
    if (this.isEmailPreview(m)) {
      const emailWidget = this.getEmailPreviewData(m);
      const fromObj = emailWidget?.from?.[0];
      if (fromObj?.name || fromObj?.address) {
        return fromObj.name || fromObj.address;
      }
    }
    const author = m?.author || m?.created_by || m?.sender;
    if (typeof author === "string") return author;
    return (
      author?.display_name ||
      author?.full_name ||
      author?.name ||
      author?.email ||
      author?.display_id ||
      "Support Team"
    );
  }

  getAuthorInitials(m: any): string {
    const name = this.getMessageAuthor(m);
    if (!name || name === "Support Team") return "ST";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  getMessageEmail(m: any): string {
    if (this.isEmailPreview(m)) {
      const emailWidget = this.getEmailPreviewData(m);
      const fromObj = emailWidget?.from?.[0];
      if (fromObj?.address) return fromObj.address;
    }
    const createdBy = m?.created_by;
    return createdBy?.email || "";
  }

  getMessageDate(m: any): string {
    const raw = m?.created_date || m?.timestamp || m?.created_at;
    if (!raw) return "";
    return moment(raw).isValid()
      ? moment(raw).format("DD-MMM-YYYY, hh:mm A")
      : raw;
  }

  getMessageBody(m: any): string {
    const body = m?.body ?? m?.comment ?? m?.message ?? m?.content ?? "";
    if (typeof body === "string") return body;
    if (body && typeof body === "object") {
      return body.text || body.value || body.content || "";
    }
    return body == null ? "" : String(body);
  }

  isEmailPreview(m: any): boolean {
    const firstWidget = m?.snap_widget_body?.[0];
    return Boolean(
      firstWidget &&
        (firstWidget.type === "email_preview" ||
          firstWidget.name === "email_preview" ||
          firstWidget.namespace === "email_preview")
    );
  }

  getEmailPreviewData(m: any): any {
    return m?.snap_widget_body?.[0] || null;
  }

  getEmailSubject(m: any): string {
    const preview = this.getEmailPreviewData(m);
    return preview?.subject || "";
  }

  getEmailFrom(m: any): string {
    const preview = this.getEmailPreviewData(m);
    const fromList = preview?.from;
    if (!fromList || !Array.isArray(fromList) || fromList.length === 0) return "";
    return fromList
      .map((f: any) => (f.name ? `${f.name} <${f.address}>` : f.address))
      .filter(Boolean)
      .join(", ");
  }

  getEmailTo(m: any): string {
    const preview = this.getEmailPreviewData(m);
    const toList = preview?.to;
    if (!toList || !Array.isArray(toList) || toList.length === 0) return "";
    return toList
      .map((t: any) => (t.name ? `${t.name} <${t.address}>` : t.address))
      .filter(Boolean)
      .join(", ");
  }

  getAuthorBadge(m: any): { label: string; type: string } {
    if (m?.labels && Array.isArray(m.labels) && m.labels.includes("auto-reply")) {
      return { label: "Auto-reply", type: "bot" };
    }

    const emailSender = this.getEmailPreviewData(m)?.from?.[0]?.user;
    const createdBy = m?.created_by;
    const authorType = emailSender?.type || createdBy?.type;

    if (authorType === "rev_user") {
      const orgName =
        emailSender?.rev_org?.display_name || createdBy?.rev_org?.display_name;
      return { label: orgName || "Customer", type: "customer" };
    }

    if (authorType === "dev_user") {
      return { label: "Support Agent", type: "agent" };
    }

    if (authorType === "service_account" || createdBy?.type === "service_account") {
      return { label: "Bot", type: "bot" };
    }

    return { label: "User", type: "default" };
  }

  getMessageArtifacts(m: any): any[] {
    if (Array.isArray(m?.artifacts)) return m.artifacts;
    return Array.isArray(m?.attachments) ? m.attachments : [];
  }

  formatFileSize(bytes: number): string {
    if (!bytes || isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  getMessageContentHtml(m: any): SafeHtml {
    // 1. Email Preview inside snap_widget
    if (this.isEmailPreview(m)) {
      const emailWidget = this.getEmailPreviewData(m);
      if (emailWidget?.html_body) {
        const clean = this.cleanHtmlContent(emailWidget.html_body);
        return this.sanitizer.bypassSecurityTrustHtml(clean);
      }
      if (emailWidget?.text_body) {
        return this.sanitizer.bypassSecurityTrustHtml(
          this.formatTextMessage(emailWidget.text_body)
        );
      }
    }

    // 2. Generic snap_widget
    if (m?.snap_widget_body && Array.isArray(m.snap_widget_body) && m.snap_widget_body.length > 0) {
      const firstWidget = m.snap_widget_body[0];
      if (firstWidget?.html_body) {
        const clean = this.cleanHtmlContent(firstWidget.html_body);
        return this.sanitizer.bypassSecurityTrustHtml(clean);
      }
      if (firstWidget?.text_body) {
        return this.sanitizer.bypassSecurityTrustHtml(
          this.formatTextMessage(firstWidget.text_body)
        );
      }
    }

    // 3. Text Body (with reference resolution)
    let rawBody = m?.body ?? m?.comment ?? m?.message ?? m?.content ?? "";
    if (typeof rawBody === "object" && rawBody !== null) {
      rawBody = rawBody.text || rawBody.value || rawBody.content || JSON.stringify(rawBody);
    }

    return this.sanitizer.bypassSecurityTrustHtml(
      this.formatTextMessage(String(rawBody), m?.references)
    );
  }

  private cleanHtmlContent(rawHtml: string): string {
    if (!rawHtml) return "";
    let clean = this.decodeHtmlEntities(rawHtml);
    clean = clean
      .replace(/<!DOCTYPE[^>]*>/gi, "")
      .replace(/<\/?(html|head|body|meta|title)[^>]*>/gi, "")
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<xml[^>]*>[\s\S]*?<\/xml>/gi, "");

    return clean.trim();
  }

  private decodeHtmlEntities(encoded: string): string {
    if (!encoded) return "";
    let decoded = encoded;
    for (let i = 0; i < 2; i++) {
      const prev = decoded;
      decoded = decoded
        .replace(/&#x3C;/gi, "<")
        .replace(/&#x3E;/gi, ">")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&#x22;/gi, '"')
        .replace(/&quot;/gi, '"')
        .replace(/&#34;/gi, '"')
        .replace(/&#x27;/gi, "'")
        .replace(/&#39;/gi, "'")
        .replace(/&#xA0;/gi, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&");
      if (decoded === prev) break;
    }
    return decoded;
  }

  private formatTextMessage(rawBody: string, references?: any[]): string {
    if (!rawBody) return "";

    let processed = rawBody;
    const refMap: { [key: string]: string } = {};

    if (references && Array.isArray(references)) {
      references.forEach((ref, idx) => {
        if (ref?.id && ref?.display_id) {
          const token = `__TICKET_REF_${idx}__`;
          refMap[token] = `<span class="ticket-mention">${ref.display_id}</span>`;
          processed = processed.split(`<${ref.id}>`).join(token);
        }
      });
    }

    processed = processed.replace(/<don:[^>]+:ticket\/(\d+)>/g, "Ticket #$1");

    let escaped = processed
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    Object.keys(refMap).forEach((token) => {
      escaped = escaped.split(token).join(refMap[token]);
    });

    escaped = escaped.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    return escaped.replace(/\n/g, "<br/>");
  }

  // ─── Charts Rendering ───────────────────────────────────────────────────────

  private scheduleChartRender(): void {
    if (!this.chartsReady) return;
    if (this.chartRenderTimer) {
      clearTimeout(this.chartRenderTimer);
    }
    this.chartRenderTimer = setTimeout(() => {
      this.renderCharts();
    }, 120);
  }

  private renderCharts(): void {
    this.renderVolumeByPriorityChart();
    this.renderSlaComplianceOverallChart();
    this.renderSlaComplianceByPriorityChart();
    this.renderTicketsByCreationTimeChart();
  }

  setDashboardContentView(view: "charts" | "tables"): void {
    this.dashboardContentView = view;
    if (view === "charts") {
      setTimeout(() => this.resizeCharts(), 0);
    }
  }

  private renderVolumeByPriorityChart(): void {
    const rows = this.volumeData.filter((row) => row.count > 0);
    const data = rows.map((row, index) => {
      const priority = row.priority || "Unknown";
      return {
        name: priority,
        value: Number((row.percentage.value * 100).toFixed(2)),
        ticketCount: row.count,
        itemStyle: {
          color: this.getPriorityColor(priority, index),
        },
      };
    });

    if (!data.length) {
      this.setNoDataChart(
        "volumeByPriority",
        this.volumeByPriorityChart,
        "Volume by Priority"
      );
      return;
    }

    this.setChartOption("volumeByPriority", this.volumeByPriorityChart, {
      title: this.getChartTitle("Volume by Priority"),
      color: this.defaultChartColors,
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          const point = params.data || {};
          return `${params.name}<br/>${point.value}% (${point.ticketCount} tickets)`;
        },
      },
      legend: {
        bottom: 0,
        left: "center",
      },
      series: [
        {
          name: "Priority",
          type: "pie",
          radius: ["38%", "68%"],
          center: ["50%", "48%"],
          avoidLabelOverlap: true,
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            formatter: (params: any) => `${params.name}: ${params.value}%`,
          },
          data,
        },
      ],
    });
  }

  private renderSlaComplianceOverallChart(): void {
    const rows = this.slaOverallData;
    const categories = rows.map((row) => row.sla_type);
    const metData = rows.map((row) => row.met);
    const breachedData = rows.map((row) => row.breached);

    if (!rows.length) {
      this.setNoDataChart(
        "slaComplianceOverall",
        this.slaComplianceOverallChart,
        "SLA Compliance - Overall"
      );
      return;
    }

    this.setChartOption("slaComplianceOverall", this.slaComplianceOverallChart, {
      title: this.getChartTitle("SLA Compliance - Overall"),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
      },
      legend: { bottom: 0 },
      grid: {
        top: 50,
        right: 30,
        bottom: 45,
        left: 110,
        containLabel: false,
      },
      xAxis: {
        type: "value",
        splitLine: {
          show: true,
          lineStyle: { color: "#eef2f7" },
        },
      },
      yAxis: {
        type: "category",
        data: categories,
      },
      series: [
        {
          name: "Met",
          type: "bar",
          data: metData,
          itemStyle: { color: this.metColor },
          label: {
            show: true,
            position: "inside",
            color: "#ffffff",
            fontWeight: "bold",
          },
        },
        {
          name: "Breached",
          type: "bar",
          data: breachedData,
          itemStyle: { color: this.breachedColor },
          label: {
            show: true,
            position: "inside",
            color: "#ffffff",
            fontWeight: "bold",
          },
        },
      ],
    });
  }

  private renderSlaComplianceByPriorityChart(): void {
    const rows = this.slaPriorityData;
    const categories = rows.map((row) => row.priority);
    const resolutionData = rows.map((row) =>
      Number((row.resolution_percentage.value * 100).toFixed(2))
    );
    const responseData = rows.map((row) =>
      Number((row.response_percentage.value * 100).toFixed(2))
    );

    if (!rows.length) {
      this.setNoDataChart(
        "slaComplianceByPriority",
        this.slaComplianceByPriorityChart,
        "SLA Compliance by Priority"
      );
      return;
    }

    this.setChartOption(
      "slaComplianceByPriority",
      this.slaComplianceByPriorityChart,
      {
        title: this.getChartTitle("SLA Compliance by Priority"),
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params: any) => {
            const list = Array.isArray(params) ? params : [params];
            const name = list[0]?.name || "";
            const lines = list.map((item: any) => `${item.seriesName}: ${item.value}%`);
            return [name, ...lines].join("<br/>");
          },
        },
        legend: { bottom: 0 },
        grid: {
          top: 50,
          right: 30,
          bottom: 45,
          left: 110,
          containLabel: false,
        },
        xAxis: {
          type: "value",
          max: 100,
          axisLabel: { formatter: "{value}%" },
          splitLine: { show: true, lineStyle: { color: "#eef2f7" } },
        },
        yAxis: {
          type: "category",
          data: categories,
        },
        series: [
          {
            name: "Resolution SLA %",
            type: "bar",
            barMaxWidth: 18,
            data: resolutionData,
            itemStyle: { color: this.resolutionColor },
            label: {
              show: true,
              position: "right",
              formatter: "{c}%",
              fontSize: 11,
            },
          },
          {
            name: "Response SLA %",
            type: "bar",
            barMaxWidth: 18,
            data: responseData,
            itemStyle: { color: this.responseColor },
            label: {
              show: true,
              position: "right",
              formatter: "{c}%",
              fontSize: 11,
            },
          },
        ],
      }
    );
  }

  private renderTicketsByCreationTimeChart(): void {
    const rows = this.creationTimeData.filter(
      (row) => row.ticket_count > 0 && row.time_period?.toLowerCase() !== "total"
    );
    const data = rows.map((row, index) => {
      return {
        name: row.time_period,
        value: Number((row.percentage.value * 100).toFixed(2)),
        ticketCount: row.ticket_count,
        itemStyle: {
          color:
            this.defaultChartColors[index % this.defaultChartColors.length],
        },
      };
    });

    if (!data.length) {
      this.setNoDataChart(
        "ticketsByCreationTime",
        this.ticketsByCreationTimeChart,
        "Tickets by Creation Time"
      );
      return;
    }

    this.setChartOption(
      "ticketsByCreationTime",
      this.ticketsByCreationTimeChart,
      {
        title: this.getChartTitle("Tickets by Creation Time"),
        color: this.defaultChartColors,
        tooltip: {
          trigger: "item",
          formatter: (params: any) => {
            const point = params.data || {};
            return `${params.name}<br/>${point.value}% (${point.ticketCount} tickets)`;
          },
        },
        legend: { bottom: 0, left: "center" },
        series: [
          {
            name: "Creation Time",
            type: "pie",
            radius: ["38%", "68%"],
            center: ["50%", "48%"],
            avoidLabelOverlap: true,
            itemStyle: { borderColor: "#fff", borderWidth: 2 },
            label: {
              formatter: (params: any) => `${params.name}: ${params.value}%`,
            },
            data,
          },
        ],
      }
    );
  }

  private setChartOption(
    key: string,
    chartElement: ElementRef<HTMLDivElement>,
    option: echarts.EChartsOption
  ): void {
    if (!chartElement?.nativeElement) return;
    let chart = this.charts[key];
    if (!chart) {
      chart = echarts.init(chartElement.nativeElement);
      this.charts[key] = chart;
      this.attachChartEvents(key, chart);
    }
    chart.setOption(option as any, true);
  }

  private attachChartEvents(key: string, chart: echarts.ECharts): void {
    chart.on("click", (params: any) => {
      if (key === "volumeByPriority") {
        const priority = params.name || params.data?.name;
        const count = params.data?.ticketCount;
        if (priority) {
          void this.openDrilldown({
            drilldown: "by_priority",
            priority,
            title: `Priority: ${priority}`,
            expectedTotal: count,
          });
        }
      } else if (key === "slaComplianceOverall") {
        const slaType = params.name;
        const isResolution = String(slaType).toLowerCase().includes("resolution");
        const outcome = params.seriesName === "Met" ? "hit" : "miss";
        void this.openDrilldown({
          drilldown: "sla",
          sla: isResolution ? "resolution" : "response",
          outcome,
          title: `${slaType} - ${params.seriesName}`,
          expectedTotal: params.value,
        });
      } else if (key === "slaComplianceByPriority") {
        const priority = params.name;
        const isResolution = String(params.seriesName)
          .toLowerCase()
          .includes("resolution");
        const row = this.slaPriorityData.find(
          (item) => item.priority === priority
        );
        const sla = isResolution ? "resolution" : "response";
        const total = isResolution
          ? row?.resolution_met + row?.resolution_breached
          : row?.response_met + row?.response_breached;

        if (priority && row) {
          void this.openDrilldown({
            drilldown: "sla_by_priority",
            priority,
            sla,
            outcome: "all",
            title: `${priority} - ${isResolution ? "Resolution" : "Response"} SLA`,
            expectedTotal: total,
          });
        }
      } else if (key === "ticketsByCreationTime") {
        const period = params.name?.toLowerCase().includes("after")
          ? "after"
          : "business";
        void this.openDrilldown({
          drilldown: "by_hour_band",
          period,
          title: `Creation Time: ${params.name}`,
          expectedTotal: params.data?.ticketCount,
        });
      }
    });
  }

  private setNoDataChart(
    key: string,
    chartElement: ElementRef<HTMLDivElement>,
    title: string
  ): void {
    if (!chartElement?.nativeElement) return;
    this.setChartOption(key, chartElement, {
      title: this.getChartTitle(title),
      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          text: "No data found for this period",
          fill: "#666",
          fontSize: 14,
        },
      },
      xAxis: { show: false },
      yAxis: { show: false },
      series: [],
    });
  }

  private getChartTitle(text: string): echarts.TitleComponentOption {
    return {
      text,
      left: "center",
      textStyle: {
        color: "#0f172a",
        fontSize: 14,
        fontWeight: "bold",
      },
    };
  }

  private getPriorityColor(priority: string, fallbackIndex = 0): string {
    const key = (priority || "").trim().toLowerCase();
    return (
      this.priorityColors[key] ||
      this.defaultChartColors[fallbackIndex % this.defaultChartColors.length]
    );
  }

  private resizeCharts = (): void => {
    Object.values(this.charts).forEach((chart) => {
      chart?.resize();
    });
  };

  private disposeCharts(): void {
    Object.values(this.charts).forEach((chart) => {
      chart?.dispose();
    });
    this.charts = {};
  }

  prepareDownloadCharts = async (): Promise<void> => {
    this.renderCharts();
    await new Promise((resolve) => setTimeout(resolve, 300));
  };

  getDownloadChartImageUrl = (key: string): string => {
    const chart = this.charts[key];
    if (!chart) return "";
    return chart.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });
  };
}
